import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/apiClient';
import { User, Lock, Trash2, Bell, Mail, Shield, Save, CheckCircle, Smartphone, CreditCard, ExternalLink, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Toggle = ({ label, description, enabled, onChange }) => (
    <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
        <div className="pr-4">
            <h4 className="text-sm font-bold text-slate-800">{label}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
        <button 
            onClick={() => onChange(!enabled)} 
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none shadow-inner ${enabled ? 'bg-indigo-600' : 'bg-slate-200'}`}
        >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
    </div>
);

const ProfilePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('BILLING');
    
    // States Compte & Sécurité
    const [user, setUser] = useState(null);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [pwdMessage, setPwdMessage] = useState({ text: '', type: '' });
    
    // States Facturation (Stripe Portal)
    const [portalLoading, setPortalLoading] = useState(false);
    const [portalError, setPortalError] = useState('');
    const [portalWarning, setPortalWarning] = useState('');

    // States Préférences
    const [prefs, setPrefs] = useState({
        newsletter_subscribed: true, alert_dca: true, alert_dividends: true,
        alert_technical: true, email_weekly_report: true, email_urgent_alerts: true
    });
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    useEffect(() => {
        fetchProfileData();
    }, []);

    const fetchProfileData = async () => {
        try {
            const userRes = await api.get('/auth/me');
            setUser(userRes.data);
            const prefsRes = await api.get('/auth/preferences');
            setPrefs(prefsRes.data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenPortal = async () => {
        setPortalLoading(true);
        setPortalError('');
        setPortalWarning('');
        try {
            const res = await api.post('/stripe/create-portal-session', { user_id: user.id });
            if (res.data.portal_url) {
                window.location.href = res.data.portal_url;
            }
        } catch (err) {
            // Si c'est notre erreur 400 personnalisée gérée côté backend (compte manuel)
            if (err.response?.status === 400 && err.response?.data?.detail?.includes('manuellement')) {
                setPortalWarning(err.response.data.detail);
            } else {
                setPortalError(err.response?.data?.detail || "Impossible d'ouvrir le portail pour le moment. Avez-vous un abonnement actif ?");
            }
        } finally {
            setPortalLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        try {
            await api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword });
            setPwdMessage({ text: "Mot de passe modifié avec succès.", type: 'success' });
            setOldPassword(''); setNewPassword('');
        } catch (err) {
            setPwdMessage({ text: err.response?.data?.detail || "Erreur lors de la modification", type: 'error' });
        }
    };

    const handlePrefChange = (key, value) => {
        setPrefs(prev => ({ ...prev, [key]: value }));
    };

    const savePreferences = async () => {
        setSavingPrefs(true);
        setSaveMessage('');
        try {
            await api.put('/auth/preferences', prefs);
            setSaveMessage("Préférences sauvegardées !");
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (err) {
            alert("Erreur détaillée: " + err.response?.data?.detail || err.message);
        } finally {
            setSavingPrefs(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (window.confirm("Action irréversible. Voulez-vous vraiment supprimer votre compte et toutes vos données ?")) {
            try {
                await api.delete('/auth/me');
                localStorage.removeItem('token');
                navigate('/');
            } catch (err) {
                alert("Erreur lors de la suppression du compte.");
            }
        }
    };

    if (!user) return <div className="min-h-screen pt-24 text-center font-bold text-slate-500 flex items-center justify-center">Chargement...</div>;

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-12 text-slate-900 font-sans">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* Header Profil (Modernisé) */}
                <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm mb-8 flex items-center gap-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-2 h-full bg-indigo-600"></div>
                    <div className="w-24 h-24 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center border-4 border-white shadow-md shrink-0">
                        <User size={40} strokeWidth={2} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-900">{user.full_name || "Investisseur"}</h1>
                        <p className="text-slate-500 font-medium mt-1">{user.email}</p>
                        <div className="mt-3 flex gap-2">
                            {user.is_pro ? (
                                <span className="bg-emerald-100 text-emerald-700 text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest border border-emerald-200 shadow-sm">PRO Actif</span>
                            ) : (
                                <span className="bg-slate-100 text-slate-600 text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest border border-slate-200">Plan Gratuit</span>
                            )}
                            {user.is_admin && <span className="bg-slate-900 text-white text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest shadow-sm">Admin</span>}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    
                    {/* Navigation Latérale */}
                    <div className="md:col-span-1 space-y-2">
                        <button onClick={() => setActiveTab('BILLING')} className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold transition-all text-sm ${activeTab === 'BILLING' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20' : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-indigo-600 border border-slate-100'}`}>
                            <CreditCard size={18} /> Abonnement
                        </button>
                        <button onClick={() => setActiveTab('NOTIFICATIONS')} className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold transition-all text-sm ${activeTab === 'NOTIFICATIONS' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20' : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-indigo-600 border border-slate-100'}`}>
                            <Bell size={18} /> Alertes & Emails
                        </button>
                        <button onClick={() => setActiveTab('SECURITY')} className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold transition-all text-sm ${activeTab === 'SECURITY' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20' : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-indigo-600 border border-slate-100'}`}>
                            <Shield size={18} /> Sécurité
                        </button>
                    </div>

                    {/* Contenu Principal */}
                    <div className="md:col-span-3">
                        
                        {/* ONGLET : ABONNEMENT & FACTURATION (REFONTE) */}
                        {activeTab === 'BILLING' && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                                    <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
                                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl border border-indigo-100 shadow-inner">
                                            <CreditCard size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-black text-xl text-slate-900 tracking-tight">Abonnement & Facturation</h3>
                                            <p className="text-sm font-medium text-slate-500 mt-1 flex items-center gap-2">
                                                Statut actuel : 
                                                {user.is_pro ? (
                                                    <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider border border-emerald-200">PRO Actif</span>
                                                ) : (
                                                    <span className="bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider border border-slate-200">Gratuit</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <p className="text-slate-600 font-medium leading-relaxed mb-8">
                                        Gérez votre abonnement KeyForIn PRO, modifiez votre carte bancaire, annulez votre plan ou téléchargez vos factures en un clic depuis notre espace sécurisé Stripe.
                                    </p>

                                    {/* Alertes dynamiques du portail */}
                                    {portalError && (
                                        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-2xl flex items-center gap-3 mb-6 shadow-sm">
                                            <AlertTriangle size={20} className="text-rose-500 shrink-0" />
                                            <p className="text-sm font-bold">{portalError}</p>
                                        </div>
                                    )}
                                    {portalWarning && (
                                        <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 p-4 rounded-2xl flex items-center gap-3 mb-6 shadow-sm">
                                            <CheckCircle size={20} className="text-indigo-500 shrink-0" />
                                            <p className="text-sm font-bold">{portalWarning}</p>
                                        </div>
                                    )}

                                    <div className="flex flex-col sm:flex-row items-center gap-4">
                                        <button 
                                            onClick={handleOpenPortal} 
                                            disabled={portalLoading}
                                            className="w-full sm:w-auto bg-slate-900 text-white px-6 py-3.5 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-slate-800 hover:scale-[1.02] transition-all shadow-lg shadow-slate-900/20 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            <ExternalLink size={18} /> {portalLoading ? "Connexion..." : "Accéder au portail client"}
                                        </button>
                                        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                            <Lock size={14} className="text-slate-300" /> Connexion chiffrée de bout en bout
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ONGLET : NOTIFICATIONS */}
                        {activeTab === 'NOTIFICATIONS' && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="bg-slate-50 px-8 py-5 border-b border-slate-100 flex items-center gap-4">
                                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl"><Smartphone size={20} /></div>
                                        <h3 className="font-black text-slate-800 text-lg">Notifications sur l'Interface</h3>
                                    </div>
                                    <div className="p-8 pt-4">
                                        <Toggle label="Moteur Quantamental (DCA)" description="Alerte lorsqu'une de vos actions devient dangereuse à conserver ou qu'une opportunité d'achat se présente." enabled={prefs.alert_dca} onChange={(v) => handlePrefChange('alert_dca', v)} />
                                        <Toggle label="Analyse Technique" description="Alerte sur les surventes (RSI bas) ou croisements de moyennes mobiles." enabled={prefs.alert_technical} onChange={(v) => handlePrefChange('alert_technical', v)} />
                                        <Toggle label="Calendrier des Dividendes" description="Rappel avant le détachement ou lors du versement de vos dividendes." enabled={prefs.alert_dividends} onChange={(v) => handlePrefChange('alert_dividends', v)} />
                                    </div>
                                </div>

                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="bg-slate-50 px-8 py-5 border-b border-slate-100 flex items-center gap-4">
                                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl"><Mail size={20} /></div>
                                        <h3 className="font-black text-slate-800 text-lg">Réception d'Emails</h3>
                                    </div>
                                    <div className="p-8 pt-4">
                                        <Toggle label="Rapport Hebdomadaire" description="Réception du bilan chiffré et des recommandations stratégiques chaque vendredi soir." enabled={prefs.email_weekly_report} onChange={(v) => handlePrefChange('email_weekly_report', v)} />
                                        <Toggle label="Alertes Urgentes" description="Email immédiat en cas de dégradation brutale des fondamentaux d'une grosse position." enabled={prefs.email_urgent_alerts} onChange={(v) => handlePrefChange('email_urgent_alerts', v)} />
                                        <Toggle label="KeyForIn Club (Newsletter)" description="Nos analyses sectorielles et articles exclusifs." enabled={prefs.newsletter_subscribed} onChange={(v) => handlePrefChange('newsletter_subscribed', v)} />
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    {saveMessage && <span className="text-emerald-600 text-sm font-bold flex items-center gap-1"><CheckCircle size={16}/> {saveMessage}</span>}
                                    <button onClick={savePreferences} disabled={savingPrefs} className="bg-indigo-600 text-white px-8 py-3.5 rounded-xl font-black flex items-center gap-2 hover:bg-indigo-700 hover:scale-105 transition-all shadow-lg shadow-indigo-600/30">
                                        <Save size={18} /> {savingPrefs ? "Sauvegarde..." : "Enregistrer les préférences"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ONGLET : SÉCURITÉ */}
                        {activeTab === 'SECURITY' && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="p-2.5 bg-slate-100 text-slate-600 rounded-xl"><Lock size={20} /></div>
                                        <h3 className="font-black text-slate-800 text-lg">Modifier le mot de passe</h3>
                                    </div>
                                    <form onSubmit={handleChangePassword} className="space-y-5 max-w-sm">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Mot de passe actuel</label>
                                            <input type="password" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-indigo-500 transition-colors" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nouveau mot de passe</label>
                                            <input type="password" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-indigo-500 transition-colors" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                        </div>
                                        {pwdMessage.text && (
                                            <div className={`text-sm font-bold p-4 rounded-xl flex items-center gap-2 ${pwdMessage.type === 'error' ? 'bg-rose-50 text-rose-700 border border-rose-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                                                {pwdMessage.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>} {pwdMessage.text}
                                            </div>
                                        )}
                                        <button type="submit" className="bg-slate-900 text-white px-8 py-3 rounded-xl text-sm font-black hover:bg-slate-800 hover:scale-105 transition-all shadow-lg shadow-slate-900/20">
                                            Mettre à jour
                                        </button>
                                    </form>
                                </div>

                                <div className="bg-rose-50 rounded-3xl border border-rose-200 p-8 shadow-sm">
                                    <div className="flex items-center gap-3 mb-3 text-rose-700">
                                        <div className="p-2 bg-rose-100 rounded-xl"><Trash2 size={20} /></div>
                                        <h3 className="font-black text-lg">Zone de danger</h3>
                                    </div>
                                    <p className="text-sm text-rose-700/80 mb-6 font-medium leading-relaxed">
                                        La suppression de votre compte entraînera la perte définitive de tous vos portefeuilles, de vos historiques de transactions et de vos configurations personnalisées.
                                    </p>
                                    <button onClick={handleDeleteAccount} className="bg-white text-rose-600 border border-rose-200 px-8 py-3 rounded-xl text-sm font-black hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors shadow-sm">
                                        Supprimer mon compte définitivement
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
